import { ProfileFormValues, UserResponse } from '@/types/user';
import { Card, Grid, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { titleCase } from '@/utils/stringCaser';
import { SVForm, SVTextInput, SVAutocomplete } from '@/components/core';
import SVPersonalBankingLinkField from '@/components/core/inputs/PersonalBankingLinkField';
import ProfileEditAvatarButton from '../ProfileEditAvatarButton';
import ProfileEditSharedInputs from '../ProfileEditSharedInputs';
import { useUpdateUser } from '@/hooks/requests/useUser';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import FormSubmissionButtons from '@/components/FormSubmissionButtons';
import { SelectOption } from '@/types/inputs';

type ProfileEditAdvisorFormProps = {
  user: UserResponse;
  teammatesList: SelectOption[];
};

export default function ProfileEditAdvisorForm({
  user,
  teammatesList,
}: ProfileEditAdvisorFormProps) {
  const navigate = useNavigate();
  const { updateAdvisor, isUpdateAdvisorPending } = useUpdateUser();
  const defaultTeammates = user.teammateInfo
    ? user.teammateInfo?.members.map(member => {
        return {
          value: member.uuid,
          label: member.fullName,
        };
      })
    : [];

  const formMethods = useForm<ProfileFormValues>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      company: user.teammateInfo?.company.name,
      role: titleCase(user.role),
      title: user.title,
      phone: user.phoneNumber,
      businessPhone: user.businessPhoneNumber,
      avatarUrl: user.avatarUrl || '',
      avatarDisplayName: '',
      supportMembers: defaultTeammates?.length === 0 ? [] : defaultTeammates,
      personalBankLink: user.teammateInfo?.alias || '',
      bio: user.bio || '',
    },
    mode: 'onBlur',
  });

  async function onSubmit(data: ProfileFormValues) {
    try {
      await updateAdvisor(data);
      showToast({
        type: 'success',
        data: {
          content: 'Your profile has been successfully updated',
        },
      });
      navigate(ROUTES.PROFILE);
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <SVForm<ProfileFormValues> methods={formMethods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Card>
          <Grid spacing={2} container>
            <Grid container size={{ xs: 12, md: 9 }}>
              <ProfileEditSharedInputs />
              <Grid size={{ xs: 12 }}>
                {user.teammateInfo && (
                  <SVPersonalBankingLinkField
                    name="personalBankLink"
                    label="Personal Banking Link"
                    advisorUrl={user.teammateInfo.advisorUrl}
                    alias={user.teammateInfo.alias}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                {teammatesList && (
                  <SVAutocomplete
                    name="supportMembers"
                    label="Support Members"
                    options={teammatesList}
                    multiple
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <SVTextInput
                  multiline
                  multilineRows={2}
                  name="bio"
                  label="Bio"
                  rules={{ maxLength: 1000 }}
                />
              </Grid>
            </Grid>
            <Grid sx={{ textAlign: 'center' }} size={{ xs: 12, md: 3 }}>
              <ProfileEditAvatarButton uuid={user.uuid} />
            </Grid>
          </Grid>
        </Card>
        <FormSubmissionButtons
          isLoading={isUpdateAdvisorPending}
          onLeave={() => navigate(ROUTES.PROFILE)}
        />
      </Stack>
    </SVForm>
  );
}
